import React, { useState, useEffect } from 'react';

// Use relative URL so Vite proxy handles routing to backend (avoids CORS issues)
// const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Helper function to format share numbers with spaces instead of commas, no decimals
const formatShares = (num: number): string => {
  return Math.floor(num).toLocaleString('en-US').replace(/,/g, ' ');
};

// Mine Production / PCAP is interstitial. Next phase is Phase 11 at $200.
// Display-only — do not read nextPhase.price_per_share (that row can be missing
// or stale and previously rendered the Phase 1 $10 leftover).
const NEXT_PHASE_PRICE_USD = 200;

interface PhaseCountdownTimerProps {
  className?: string;
  variant?: 'default' | 'compact' | 'banner' | 'banner-compact' | 'landing' | 'dashboard';
  onPurchaseClick?: () => void;
}

interface PhaseData {
  id: number;
  phase_number: number;
  phase_name: string;
  price_per_share: number;
  total_shares_available: number;
  shares_sold: number;
  total_allocated_shares?: number; // Base + commissions + bonuses
  is_active: boolean;
  start_date?: string;
  end_date?: string;
}

interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  total: number;
}

export const PhaseCountdownTimer: React.FC<PhaseCountdownTimerProps> = ({
  className = '',
  variant = 'default',
  onPurchaseClick
}) => {
  const [currentPhase, setCurrentPhase] = useState<PhaseData | null>(null);
  const [nextPhase, setNextPhase] = useState<PhaseData | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining | null>(null);
  const [loading, setLoading] = useState(true);
  const [phaseEndDate, setPhaseEndDate] = useState<Date | null>(null);

  useEffect(() => {
    fetchPhaseData();
  }, []);

  useEffect(() => {
    if (!phaseEndDate) return;

    // Update countdown immediately
    updateCountdown();

    // Update countdown every second
    const interval = setInterval(() => {
      updateCountdown();
    }, 1000);

    return () => clearInterval(interval);
  }, [phaseEndDate]);

  const fetchPhaseData = async () => {
    try {
      // Get countdown data via API
      const response = await fetch(`/api/public/phase-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ operation: 'get_phase_countdown_data' })
      });

      const result = await response.json();
      if (!response.ok || !result.data?.activePhase) {
        console.error('Error fetching active phase:', result.error);
        setLoading(false);
        return;
      }

      const activePhase = result.data.activePhase;

      // ✅ Use API endpoint for consistent calculations
      // This is the SINGLE SOURCE OF TRUTH for phase-specific share calculations
      const shareResponse = await fetch(`/api/public/share-calculations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ operation: 'get_total_allocated_shares_for_phase', data: { phaseId: activePhase.id } })
      });
      const shareResult = await shareResponse.json();

      console.log('📊 PhaseCountdownTimer - Using API endpoint:', {
        phaseId: activePhase.id,
        phaseName: activePhase.phase_name,
        totalAllocated: shareResult.data?.totalAllocated,
        breakdown: shareResult.data
      });

      setCurrentPhase({
        ...activePhase,
        total_allocated_shares: shareResult.data?.totalAllocated || 0
      });

      // Dynamic phase end date from database (set by cron job or admin)
      // Falls back to 5th of next month at 23:59:59 SAST if not set
      let endDate: Date;
      if (activePhase.end_date) {
        endDate = new Date(activePhase.end_date);
        console.log('📅 Phase end date from DB:', endDate.toISOString());
      } else {
        // Fallback: calculate 5th of next month at 23:59:59 SAST (21:59:59 UTC)
        const now = new Date();
        const nextMonth = new Date(Date.UTC(now.getFullYear(), now.getMonth() + 1, 5, 21, 59, 59));
        endDate = nextMonth;
        console.log('📅 Phase end date (fallback, 5th next month):', endDate.toISOString());
      }

      setPhaseEndDate(endDate);

      // Use next phase from API response
      if (result.data.nextPhase) {
        setNextPhase(result.data.nextPhase);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error in fetchPhaseData:', error);
      setLoading(false);
    }
  };

  const updateCountdown = () => {
    if (!phaseEndDate) return;

    const now = new Date().getTime();
    const distance = phaseEndDate.getTime() - now;

    if (distance < 0) {
      // Transition date has passed
      setTimeRemaining({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        total: 0
      });
      return;
    }

    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);

    setTimeRemaining({
      days,
      hours,
      minutes,
      seconds,
      total: distance
    });
  };

  if (loading || !currentPhase) {
    return null;
  }

  // Don't show countdown if we're past the transition date
  if (timeRemaining && timeRemaining.total <= 0) {
    return null;
  }

  // The API supplies active/confirmed purchase shares; never fall back to phase-maintained totals.
  const totalAllocated = currentPhase.total_allocated_shares ?? 0;
  const sharesRemaining = currentPhase.total_shares_available - totalAllocated;
  const percentageSold = (totalAllocated / currentPhase.total_shares_available) * 100;

  // Dashboard chip — fit-content, 13px cells, no price tiles (stash wip-countdown-4.8.267).
  if (variant === 'dashboard') {
    const cell = (value: number, label: string) => (
      <div style={{
        backgroundColor: 'rgba(217, 119, 6, 0.2)',
        border: '1px solid #d97706',
        borderRadius: '6px',
        padding: '4px 6px',
        minWidth: '28px',
        textAlign: 'center',
      }}>
        <div style={{ color: '#fbbf24', fontSize: '13px', fontWeight: 'bold', lineHeight: '1', fontFamily: 'monospace' }}>
          {String(value).padStart(2, '0')}
        </div>
        <div style={{ color: '#d97706', fontSize: '9px', fontWeight: 600, marginTop: '2px', letterSpacing: '0.4px' }}>
          {label}
        </div>
      </div>
    );
    return (
      <div className={`phase-countdown-dashboard ${className}`} style={{ width: 'fit-content', margin: '0 auto 8px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          background: 'linear-gradient(135deg, rgba(41, 37, 36, 0.95) 0%, rgba(28, 25, 23, 0.95) 100%)',
          border: '1px solid rgba(217, 119, 6, 0.5)',
          borderRadius: '8px',
        }}>
          <div style={{ color: '#fbbf24', fontSize: '13px', fontWeight: 700, letterSpacing: '0.6px', whiteSpace: 'nowrap' }}>
            FINAL COUNTDOWN
          </div>
          {timeRemaining && (
            <div style={{ display: 'flex', gap: '6px' }}>
              {cell(timeRemaining.days, 'D')}
              {cell(timeRemaining.hours, 'H')}
              {cell(timeRemaining.minutes, 'M')}
              {cell(timeRemaining.seconds, 'S')}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Compact variant for dashboard headers
  if (variant === 'compact') {
    return (
      <div className={`phase-countdown-compact ${className}`}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          padding: '12px 16px',
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          border: '1px solid rgba(245, 158, 11, 0.3)',
          borderRadius: '8px'
        }}>
          <div style={{ flex: 1 }}>
            <div style={{
              color: '#fbbf24',
              fontSize: '14px',
              fontWeight: 'bold',
              marginBottom: '4px',
              textShadow: '0 0 10px rgba(251, 191, 36, 0.8), 0 0 20px rgba(251, 191, 36, 0.5), 0 0 30px rgba(251, 191, 36, 0.3)',
              animation: 'neonPulse 2s ease-in-out infinite'
            }}>
              🔥 FINAL COUNTDOWN
            </div>
            <div style={{ color: '#ffffff', fontSize: '13px' }}>
              ${currentPhase.price_per_share}/share → ${NEXT_PHASE_PRICE_USD}/share
            </div>
          </div>
          {timeRemaining && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <TimeUnit value={timeRemaining.days} label="D" compact />
              <TimeUnit value={timeRemaining.hours} label="H" compact />
              <TimeUnit value={timeRemaining.minutes} label="M" compact />
              <TimeUnit value={timeRemaining.seconds} label="S" compact />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Banner variant for top of pages - Brown/Gold theme matching landing page.
  // banner-compact is the same card at 1/2 size so it does not fill the admin header.
  if (variant === 'banner' || variant === 'banner-compact') {
    const s = variant === 'banner-compact' ? 0.5 : 1;
    const cell = (value: number, label: string) => (
      <div style={{
        backgroundColor: 'rgba(217, 119, 6, 0.2)',
        border: `${2 * s}px solid #d97706`,
        borderRadius: `${8 * s}px`,
        padding: `${10 * s}px ${12 * s}px`,
        minWidth: `${60 * s}px`,
        textAlign: 'center'
      }}>
        <div style={{ color: '#fbbf24', fontSize: `${20 * s}px`, fontWeight: 'bold', lineHeight: '1' }}>
          {value}
        </div>
        <div style={{ color: '#d97706', fontSize: `${13 * s}px`, fontWeight: '600', marginTop: `${4 * s}px` }}>
          {label}
        </div>
      </div>
    );
    return (
      <div className={`phase-countdown-banner ${className}`}>
        <div style={{
          background: 'linear-gradient(135deg, rgba(41, 37, 36, 0.95) 0%, rgba(28, 25, 23, 0.95) 100%)',
          border: `${2 * s}px solid rgba(217, 119, 6, 0.5)`,
          borderRadius: `${12 * s}px`,
          padding: `${20 * s}px`,
          boxShadow: `0 ${8 * s}px ${32 * s}px rgba(0, 0, 0, 0.4)`,
          backdropFilter: 'blur(10px)',
          maxWidth: `${1200 * s}px`,
          margin: '0 auto'
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: `${16 * s}px`
          }}>
            <div style={{
              color: '#fbbf24',
              fontSize: `${28 * s}px`,
              fontWeight: '700',
              letterSpacing: `${2 * s}px`,
              marginBottom: `${6 * s}px`,
              textTransform: 'uppercase',
              textShadow: '0 0 10px rgba(251, 191, 36, 0.9), 0 0 20px rgba(251, 191, 36, 0.6), 0 0 30px rgba(251, 191, 36, 0.4), 0 0 40px rgba(251, 191, 36, 0.2)',
              animation: 'neonPulse 2s ease-in-out infinite'
            }}>
              🔥 FINAL COUNTDOWN
            </div>
            <div style={{
              color: '#d97706',
              fontSize: `${25 * s}px`,
              fontWeight: '600'
            }}>
              Current: <span style={{ color: '#10b981', fontWeight: 'bold' }}>${currentPhase.price_per_share}/share</span> • Next: <span style={{ color: '#fbbf24', fontWeight: 'bold' }}>{nextPhase?.phase_name || 'Phase 11'}</span> at <span style={{ color: '#ef4444', fontWeight: 'bold' }}>${NEXT_PHASE_PRICE_USD}/share</span>
            </div>
          </div>

          {timeRemaining && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: `${10 * s}px`,
              marginBottom: `${16 * s}px`
            }}>
              {cell(timeRemaining.days, 'DAYS')}
              {cell(timeRemaining.hours, 'HRS')}
              {cell(timeRemaining.minutes, 'MINS')}
              {cell(timeRemaining.seconds, 'SECS')}
            </div>
          )}

          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: `${8 * s}px`,
              fontSize: `${15 * s}px`,
              fontWeight: '600'
            }}>
              <span style={{ color: '#10b981' }}>
                Shares Sold: {formatShares(totalAllocated)}
              </span>
              <span style={{ color: '#fbbf24' }}>
                Available: {formatShares(sharesRemaining)}
              </span>
            </div>
            <div style={{
              width: '100%',
              height: `${8 * s}px`,
              backgroundColor: 'rgba(28, 25, 23, 0.8)',
              borderRadius: `${4 * s}px`,
              overflow: 'hidden',
              border: '1px solid rgba(217, 119, 6, 0.3)'
            }}>
              <div style={{
                width: `${percentageSold}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #10b981 0%, #fbbf24 100%)',
                transition: 'width 0.5s ease',
                boxShadow: '0 0 8px rgba(251, 191, 36, 0.5)'
              }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Landing page variant - Brown/Gold theme to match affiliate landing page
  if (variant === 'landing') {
    return (
      <div className={`phase-countdown-landing ${className}`}>
        <div style={{
          background: 'linear-gradient(135deg, rgba(41, 37, 36, 0.95) 0%, rgba(28, 25, 23, 0.95) 100%)',
          border: '2px solid rgba(217, 119, 6, 0.5)',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(10px)'
        }}>
          {/* Header */}
          <div style={{
            textAlign: 'center',
            marginBottom: '16px'
          }}>
            <div style={{
              color: '#fbbf24',
              fontSize: '28px',
              fontWeight: '700',
              letterSpacing: '2px',
              marginBottom: '6px',
              textTransform: 'uppercase',
              textShadow: '0 0 10px rgba(251, 191, 36, 0.9), 0 0 20px rgba(251, 191, 36, 0.6), 0 0 30px rgba(251, 191, 36, 0.4), 0 0 40px rgba(251, 191, 36, 0.2)',
              animation: 'neonPulse 2s ease-in-out infinite'
            }}>
              🔥 FINAL COUNTDOWN
            </div>
            <div style={{
              color: '#d97706',
              fontSize: '25px',
              fontWeight: '600'
            }}>
              Current: <span style={{ color: '#10b981', fontWeight: 'bold' }}>${currentPhase.price_per_share}/share</span> • Next: <span style={{ color: '#ef4444', fontWeight: 'bold' }}>${NEXT_PHASE_PRICE_USD}/share</span>
            </div>
          </div>

          {/* Countdown Timer */}
          {timeRemaining && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '10px',
              marginBottom: '16px'
            }}>
              <div style={{
                backgroundColor: 'rgba(217, 119, 6, 0.2)',
                border: '2px solid #d97706',
                borderRadius: '8px',
                padding: '10px 12px',
                minWidth: '60px',
                textAlign: 'center'
              }}>
                <div style={{ color: '#fbbf24', fontSize: '20px', fontWeight: 'bold', lineHeight: '1' }}>
                  {timeRemaining.days}
                </div>
                <div style={{ color: '#d97706', fontSize: '13px', fontWeight: '600', marginTop: '4px' }}>
                  DAYS
                </div>
              </div>
              <div style={{
                backgroundColor: 'rgba(217, 119, 6, 0.2)',
                border: '2px solid #d97706',
                borderRadius: '8px',
                padding: '10px 12px',
                minWidth: '60px',
                textAlign: 'center'
              }}>
                <div style={{ color: '#fbbf24', fontSize: '20px', fontWeight: 'bold', lineHeight: '1' }}>
                  {timeRemaining.hours}
                </div>
                <div style={{ color: '#d97706', fontSize: '13px', fontWeight: '600', marginTop: '4px' }}>
                  HRS
                </div>
              </div>
              <div style={{
                backgroundColor: 'rgba(217, 119, 6, 0.2)',
                border: '2px solid #d97706',
                borderRadius: '8px',
                padding: '10px 12px',
                minWidth: '60px',
                textAlign: 'center'
              }}>
                <div style={{ color: '#fbbf24', fontSize: '20px', fontWeight: 'bold', lineHeight: '1' }}>
                  {timeRemaining.minutes}
                </div>
                <div style={{ color: '#d97706', fontSize: '13px', fontWeight: '600', marginTop: '4px' }}>
                  MINS
                </div>
              </div>
              <div style={{
                backgroundColor: 'rgba(217, 119, 6, 0.2)',
                border: '2px solid #d97706',
                borderRadius: '8px',
                padding: '10px 12px',
                minWidth: '60px',
                textAlign: 'center'
              }}>
                <div style={{ color: '#fbbf24', fontSize: '20px', fontWeight: 'bold', lineHeight: '1' }}>
                  {timeRemaining.seconds}
                </div>
                <div style={{ color: '#d97706', fontSize: '13px', fontWeight: '600', marginTop: '4px' }}>
                  SECS
                </div>
              </div>
            </div>
          )}

          {/* Shares Progress */}
          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '8px',
              fontSize: '15px',
              fontWeight: '600'
            }}>
              <span style={{ color: '#10b981' }}>
                Shares Sold: {formatShares(totalAllocated)}
              </span>
              <span style={{ color: '#fbbf24' }}>
                Available: {formatShares(sharesRemaining)}
              </span>
            </div>
            <div style={{
              width: '100%',
              height: '8px',
              backgroundColor: 'rgba(28, 25, 23, 0.8)',
              borderRadius: '4px',
              overflow: 'hidden',
              border: '1px solid rgba(217, 119, 6, 0.3)'
            }}>
              <div style={{
                width: `${percentageSold}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #10b981 0%, #fbbf24 100%)',
                transition: 'width 0.5s ease',
                boxShadow: '0 0 8px rgba(251, 191, 36, 0.5)'
              }} />
            </div>
          </div>

          {/* Purchase Shares Button */}
          {onPurchaseClick && (
            <button
              onClick={onPurchaseClick}
              style={{
                width: '100%',
                marginTop: '16px',
                padding: '14px 24px',
                background: 'linear-gradient(135deg, #fbbf24 0%, #d97706 100%)',
                border: '2px solid #fbbf24',
                borderRadius: '8px',
                color: '#1c1917',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                boxShadow: '0 4px 12px rgba(251, 191, 36, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(251, 191, 36, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(251, 191, 36, 0.3)';
              }}
            >
              Purchase Shares
            </button>
          )}
        </div>
      </div>
    );
  }

  // Default variant - full featured
  return (
    <div className={`phase-countdown-timer ${className}`}>
      <div style={{
        backgroundColor: 'rgba(31, 41, 55, 0.9)',
        borderRadius: '12px',
        padding: '24px',
        border: '1px solid #374151',
        marginBottom: '24px'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <h3 style={{
            color: '#fbbf24',
            fontSize: '28px',
            fontWeight: 'bold',
            marginBottom: '8px',
            textShadow: '0 0 10px rgba(251, 191, 36, 0.9), 0 0 20px rgba(251, 191, 36, 0.6), 0 0 30px rgba(251, 191, 36, 0.4), 0 0 40px rgba(251, 191, 36, 0.2)',
            animation: 'neonPulse 2s ease-in-out infinite'
          }}>
            🔥 FINAL COUNTDOWN
          </h3>
          <p style={{ color: '#ffffff', fontSize: '14px' }}>
            Lock in your shares at ${currentPhase.price_per_share} before the price increases
          </p>
        </div>

        {/* Countdown Display */}
        {timeRemaining && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '16px',
            marginBottom: '24px'
          }}>
            <TimeUnit value={timeRemaining.days} label="Days" />
            <TimeUnit value={timeRemaining.hours} label="Hours" />
            <TimeUnit value={timeRemaining.minutes} label="Minutes" />
            <TimeUnit value={timeRemaining.seconds} label="Seconds" />
          </div>
        )}

        {/* Phase Information */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '16px',
          marginBottom: '20px'
        }}>
          <div style={{
            padding: '16px',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{ color: '#3b82f6', fontSize: '28px', fontWeight: 'bold' }}>
              ${currentPhase.price_per_share}
            </div>
            <div style={{ color: '#ffffff', fontSize: '14px', marginTop: '4px' }}>
              Current Price
            </div>
          </div>
          <div style={{
            padding: '16px',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{ color: '#ef4444', fontSize: '28px', fontWeight: 'bold' }}>
              ${NEXT_PHASE_PRICE_USD}
            </div>
            <div style={{ color: '#ffffff', fontSize: '14px', marginTop: '4px' }}>
              Next Phase Price
            </div>
          </div>
        </div>

        {/* Shares Availability */}
        <div style={{
          padding: '16px',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          borderRadius: '8px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '8px'
          }}>
            <span style={{ color: '#10b981', fontSize: '14px', fontWeight: 'bold' }}>
              Shares Sold: {formatShares(totalAllocated)}
            </span>
            <span style={{ color: '#10b981', fontSize: '14px', fontWeight: 'bold' }}>
              Available: {formatShares(sharesRemaining)}
            </span>
          </div>
          <div style={{
            width: '100%',
            height: '8px',
            backgroundColor: 'rgba(55, 65, 81, 0.5)',
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${percentageSold}%`,
              height: '100%',
              backgroundColor: '#10b981',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>
      </div>
    </div>
  );
};

// Time Unit Component
const TimeUnit: React.FC<{ value: number; label: string; compact?: boolean }> = ({
  value,
  label,
  compact = false
}) => {
  if (compact) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '60px',
        padding: '8px 12px',
        background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(245, 158, 11, 0.05) 100%)',
        border: '2px solid rgba(245, 158, 11, 0.4)',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
      }}>
        <div style={{
          color: '#f59e0b',
          fontSize: '24px',
          fontWeight: 'bold',
          lineHeight: '1',
          fontFamily: 'monospace',
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)'
        }}>
          {String(value).padStart(2, '0')}
        </div>
        <div style={{
          color: '#ffffff',
          fontSize: '13px',
          marginTop: '4px',
          fontWeight: '600',
          letterSpacing: '0.5px'
        }}>
          {label}
        </div>
      </div>
    );
  }

  return (
    <div style={{
      padding: '16px',
      backgroundColor: 'rgba(245, 158, 11, 0.1)',
      border: '2px solid rgba(245, 158, 11, 0.3)',
      borderRadius: '8px',
      textAlign: 'center'
    }}>
      <div style={{
        color: '#f59e0b',
        fontSize: '36px',
        fontWeight: 'bold',
        lineHeight: '1',
        marginBottom: '8px'
      }}>
        {String(value).padStart(2, '0')}
      </div>
      <div style={{
        color: '#ffffff',
        fontSize: '14px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
      }}>
        {label}
      </div>
    </div>
  );
};

// Add CSS animation for pulsing neon glow
const style = document.createElement('style');
style.textContent = `
  @keyframes neonPulse {
    0%, 100% {
      text-shadow:
        0 0 10px rgba(251, 191, 36, 0.9),
        0 0 20px rgba(251, 191, 36, 0.6),
        0 0 30px rgba(251, 191, 36, 0.4),
        0 0 40px rgba(251, 191, 36, 0.2);
    }
    50% {
      text-shadow:
        0 0 15px rgba(251, 191, 36, 1),
        0 0 30px rgba(251, 191, 36, 0.8),
        0 0 45px rgba(251, 191, 36, 0.6),
        0 0 60px rgba(251, 191, 36, 0.4);
    }
  }
`;
if (!document.head.querySelector('style[data-neon-pulse]')) {
  style.setAttribute('data-neon-pulse', 'true');
  document.head.appendChild(style);
}

export default PhaseCountdownTimer;

